import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { configApi } from '../api/config';
import { ConfirmDialog, type ConfirmDialogState } from '../components/ConfirmDialog';
import { DisabledActionButton } from '../components/DisabledActionButton';
import { PageHeader } from '../components/FeatureStatus';
import { EmptyState, ErrorState, LoadingState } from '../components/RequestState';
import { useAsyncData } from '../hooks/useAsyncData';
import { useIsFlowAdmin } from '../hooks/usePermission';
import type { As2TransportProfileRecord, PartnerRecord } from '../types/api';

const PARTNER_PAGE_SIZE = 50;

// Standalone entry point for AS2 transport profile management, extracted from the inline
// panel embedded in PartnersPage (src/pages/ConfigPages.tsx). That inline panel keeps
// working as-is for the partner-onboarding flow — this page is an additive, dedicated
// place to reach the same `/partners/:id/as2-transport-profiles` API without navigating
// through the full Partners admin screen first.

interface As2ProfileDraft {
  id?: string;
  environmentProfile: 'DEV' | 'TEST' | 'UAT' | 'PROD';
  code: string;
  profileName: string;
  as2Identifier: string;
  outboundEndpoint: string;
  mdnMode: 'NONE' | 'SYNCHRONOUS' | 'ASYNCHRONOUS';
  outboundSigningEnabled: boolean;
  outboundEncryptionEnabled: boolean;
  signedMdnRequired: boolean;
}

function defaultAs2ProfileDraft(env: As2ProfileDraft['environmentProfile'] = 'DEV'): As2ProfileDraft {
  return {
    environmentProfile: env,
    code: '',
    profileName: '',
    as2Identifier: '',
    outboundEndpoint: '',
    mdnMode: 'ASYNCHRONOUS',
    outboundSigningEnabled: false,
    outboundEncryptionEnabled: false,
    signedMdnRequired: false,
  };
}

function saveProfileDisabledReason(form: As2ProfileDraft | null, saving: boolean): string | null {
  if (saving) return 'A save is already in progress.';
  if (!form) return 'Open a profile to edit before saving.';
  if (!form.code.trim()) return 'Gateway profile code is required.';
  if (!form.profileName.trim()) return 'Profile name is required.';
  if (!form.as2Identifier.trim()) return 'Remote AS2 identifier is required.';
  if (!form.outboundEndpoint.trim()) return 'Remote AS2 HTTPS endpoint is required.';
  return null;
}

function profileToForm(profile: As2TransportProfileRecord): As2ProfileDraft {
  return {
    id: profile.id,
    environmentProfile: profile.environmentProfile ?? 'DEV',
    code: profile.code ?? '',
    profileName: profile.profileName ?? '',
    as2Identifier: profile.as2Identifier ?? '',
    outboundEndpoint: profile.outboundEndpoint ?? '',
    mdnMode: profile.mdnMode ?? 'ASYNCHRONOUS',
    outboundSigningEnabled: profile.outboundSigningEnabled ?? false,
    outboundEncryptionEnabled: profile.outboundEncryptionEnabled ?? false,
    signedMdnRequired: profile.signedMdnRequired ?? false,
  };
}

export function As2TransportProfilesPage() {
  const canManage = useIsFlowAdmin();
  const navigate = useNavigate();
  const { search } = useLocation();
  const requestedPartnerId = useMemo(() => new URLSearchParams(search).get('partnerId') ?? '', [search]);
  const [partnerId, setPartnerId] = useState(requestedPartnerId);
  const [rememberedPartner, setRememberedPartner] = useState<PartnerRecord | null>(null);
  const [partnerSearch, setPartnerSearch] = useState('');
  const [form, setForm] = useState<As2ProfileDraft | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);

  const { data: partnersResponse, isLoading: partnersLoading, error: partnersError } = useAsyncData(
    () => configApi.partners.list({ page: 1, pageSize: PARTNER_PAGE_SIZE, search: partnerSearch || undefined }),
    [partnerSearch],
    { debounceMs: 250 },
  );
  const partners = partnersResponse?.data ?? [];
  const partnersTotal = partnersResponse?.total ?? partners.length;
  const selectedPartner = partners.find((partner) => partner.id === partnerId) ?? (rememberedPartner?.id === partnerId ? rememberedPartner : null);

  const { data: profiles, isLoading: profilesLoading, error: profilesError, refresh } = useAsyncData(
    () => (partnerId ? configApi.partners.listAs2Profiles(partnerId) : Promise.resolve<As2TransportProfileRecord[]>([])),
    [partnerId],
  );

  useEffect(() => {
    if (requestedPartnerId && requestedPartnerId !== partnerId) {
      setPartnerId(requestedPartnerId);
      setForm(null);
      setFormError(null);
    }
  }, [partnerId, requestedPartnerId]);

  useEffect(() => {
    const current = partners.find((partner) => partner.id === partnerId);
    if (current) setRememberedPartner(current);
  }, [partnerId, partners]);

  function selectPartner(id: string) {
    setPartnerId(id);
    const params = new URLSearchParams(search);
    if (id) params.set('partnerId', id);
    else params.delete('partnerId');
    navigate({ search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
    setForm(null);
    setFormError(null);
  }

  async function saveProfile() {
    if (!form || !partnerId) return;
    setIsSaving(true);
    setFormError(null);
    try {
      await configApi.partners.saveAs2Profile(partnerId, { id: form.id, integrationPartnerId: partnerId, ...form });
      setForm(null);
      await refresh();
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : 'AS2 transport profile save failed.');
    } finally {
      setIsSaving(false);
    }
  }

  function requestSaveProfile() {
    if (saveProfileDisabledReason(form, isSaving) || !form) return;
    const isUpdate = Boolean(form.id);
    setConfirm({
      title: isUpdate ? 'Update AS2 transport profile' : 'Create AS2 transport profile',
      message: `This changes signing, encryption, and MDN settings for ${selectedPartner?.name ?? 'this partner'}'s ${form.environmentProfile} environment, affecting live AS2 traffic. This requires flow-admin.`,
      confirmLabel: isUpdate ? 'Update profile' : 'Create profile',
      risk: 'high',
      requireText: 'SAVE',
      onConfirm: () => saveProfile(),
    });
  }

  return (
    <div className="page-grid">
      <PageHeader
        eyebrow="AS2 Gateway"
        title="AS2 transport profiles"
        purpose="Configure per-partner, per-environment AS2 gateway endpoints, identifiers, and MDN settings."
        status="PRODUCTION_READY"
        connected
        next="Pick a partner to view or edit its AS2 transport profiles. This is the same data managed inline from Partners > Manage AS2."
      />
      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Select partner</h2>
            <p>Search narrows the picker but keeps the active partner loaded below.</p>
          </div>
        </div>
        {partnersLoading ? <LoadingState label="Loading partners..." /> : null}
        {partnersError ? <ErrorState message={partnersError} /> : null}
        {!partnersLoading && !partnersError ? (
          <div className="toolbar toolbar-grid">
            <input
              aria-label="Search partners"
              placeholder="Search partners by name or code..."
              value={partnerSearch}
              onChange={(event) => setPartnerSearch(event.target.value)}
            />
            <select aria-label="Partner" value={partnerId} onChange={(event) => selectPartner(event.target.value)}>
              <option value="">Select a partner...</option>
              {partners.map((partner) => (
                <option key={partner.id} value={partner.id}>{partner.name} ({partner.code})</option>
              ))}
            </select>
          </div>
        ) : null}
        {!partnersLoading && !partnersError && partnersTotal > partners.length ? (
          <p className="field-hint">
            Showing {partners.length} of {partnersTotal} partners — refine the search above to find others.
          </p>
        ) : null}
      </section>

      {partnerId && selectedPartner ? (
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>AS2 transport profiles — {selectedPartner.name}</h2>
              <p>Configure per-environment AS2 gateway endpoints, identifiers, and MDN settings.</p>
            </div>
            <div className="panel-actions">
              {!form && canManage ? (
                <button type="button" onClick={() => setForm(defaultAs2ProfileDraft(selectedPartner.environmentProfile ?? 'DEV'))}>
                  New profile
                </button>
              ) : null}
            </div>
          </div>
          {profilesLoading ? <LoadingState label="Loading AS2 transport profiles..." /> : null}
          {profilesError ? <ErrorState message={profilesError} onRetry={refresh} /> : null}
          {form ? (
            <div className="form-grid">
              <label>
                <span>Environment</span>
                <select
                  value={form.environmentProfile}
                  onChange={(event) => setForm((current) => current && { ...current, environmentProfile: event.target.value as As2ProfileDraft['environmentProfile'] })}
                >
                  {(['DEV', 'TEST', 'UAT', 'PROD'] as const).map((env) => <option key={env}>{env}</option>)}
                </select>
              </label>
              <label>
                <span>Gateway profile code</span>
                <input value={form.code} onChange={(event) => setForm((current) => current && { ...current, code: event.target.value })} />
              </label>
              <label>
                <span>Profile name</span>
                <input value={form.profileName} onChange={(event) => setForm((current) => current && { ...current, profileName: event.target.value })} />
              </label>
              <label>
                <span>Remote AS2 identifier</span>
                <input value={form.as2Identifier} onChange={(event) => setForm((current) => current && { ...current, as2Identifier: event.target.value })} />
              </label>
              <label className="full-width">
                <span>Remote AS2 HTTPS endpoint</span>
                <input value={form.outboundEndpoint} onChange={(event) => setForm((current) => current && { ...current, outboundEndpoint: event.target.value })} />
              </label>
              <label>
                <span>MDN mode</span>
                <select
                  value={form.mdnMode}
                  onChange={(event) => setForm((current) => current && { ...current, mdnMode: event.target.value as As2ProfileDraft['mdnMode'] })}
                >
                  <option value="NONE">None</option>
                  <option value="SYNCHRONOUS">Synchronous</option>
                  <option value="ASYNCHRONOUS">Asynchronous</option>
                </select>
              </label>
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={form.outboundSigningEnabled}
                  onChange={(event) => setForm((current) => current && { ...current, outboundSigningEnabled: event.target.checked })}
                />
                <span>Outbound signing</span>
              </label>
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={form.outboundEncryptionEnabled}
                  onChange={(event) => setForm((current) => current && { ...current, outboundEncryptionEnabled: event.target.checked })}
                />
                <span>Outbound encryption</span>
              </label>
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={form.signedMdnRequired}
                  onChange={(event) => setForm((current) => current && { ...current, signedMdnRequired: event.target.checked })}
                />
                <span>Signed MDN required</span>
              </label>
              {formError ? <div className="form-error full-width">{formError}</div> : null}
              <div className="form-actions full-width">
                <DisabledActionButton reason={saveProfileDisabledReason(form, isSaving)} onClick={requestSaveProfile}>
                  {isSaving ? 'Saving...' : form.id ? 'Update profile' : 'Create profile'}
                </DisabledActionButton>
                <button type="button" className="ghost-button" onClick={() => { setForm(null); setFormError(null); }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : !profilesLoading && !profilesError ? (
            profiles && profiles.length > 0 ? (
              <div className="table-scroll" tabIndex={0} aria-label={`AS2 transport profiles for ${selectedPartner.name}`}>
                <table className="data-table as2-transport-table">
                  <thead>
                    <tr><th>Code</th><th>Name</th><th>Environment</th><th>Endpoint</th><th>MDN mode</th><th>Security</th><th /></tr>
                  </thead>
                  <tbody>
                    {profiles.map((profile) => (
                      <tr key={profile.id ?? profile.code}>
                        <td data-label="Code">{profile.code}</td>
                        <td data-label="Name">{profile.profileName ?? profile.name ?? profile.code}</td>
                        <td data-label="Environment">{profile.environmentProfile}</td>
                        <td data-label="Endpoint"><span className="mono">{profile.outboundEndpoint}</span></td>
                        <td data-label="MDN mode">{profile.mdnMode}</td>
                        <td data-label="Security">
                          <span className="table-primary">
                            <span>Signing: {profile.outboundSigningEnabled ? 'Yes' : 'No'}</span>
                            <span>Encryption: {profile.outboundEncryptionEnabled ? 'Yes' : 'No'}</span>
                            <span>Signed MDN: {profile.signedMdnRequired ? 'Yes' : 'No'}</span>
                          </span>
                        </td>
                        <td data-label="Action">
                          {canManage ? (
                            <button type="button" onClick={() => setForm(profileToForm(profile))}>Edit</button>
                          ) : (
                            <span className="mono" title="Requires flow-admin">Requires flow-admin</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState label="No AS2 transport profiles for this partner yet." />
            )
          ) : null}
        </section>
      ) : partnerId && !partnersLoading ? (
        <section className="panel">
          <EmptyState label="Selected partner is not in the current search results. Clear or refine the partner search to load it." />
        </section>
      ) : null}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
